import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  crmEngine,
  type FollowUpResult,
  type LostReasonCode,
  type OpportunityStage,
} from '@/services/crm'
import { useOpportunityStore } from '@/stores/opportunity-store'

const QUERY_KEY = ['crm', 'opportunities'] as const

export function useOpportunity() {
  const queryClient = useQueryClient()
  const hydrate = useOpportunityStore((s) => s.hydrate)
  const refresh = useOpportunityStore((s) => s.refresh)
  const ready = useOpportunityStore((s) => s.ready)
  const opportunities = useOpportunityStore((s) => s.opportunities)
  const pipeline = useOpportunityStore((s) => s.pipeline)
  const filters = useOpportunityStore((s) => s.filters)
  const setFilters = useOpportunityStore((s) => s.setFilters)
  const selectedId = useOpportunityStore((s) => s.selectedId)
  const setSelectedId = useOpportunityStore((s) => s.setSelectedId)
  const contactDraft = useOpportunityStore((s) => s.contactDraft)
  const setContactDraft = useOpportunityStore((s) => s.setContactDraft)
  const responseOpen = useOpportunityStore((s) => s.responseOpen)
  const setResponseOpen = useOpportunityStore((s) => s.setResponseOpen)
  const permissions = useOpportunityStore((s) => s.permissions)

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => {
      hydrate()
      return crmEngine.list(useOpportunityStore.getState().filters)
    },
  })

  useEffect(() => {
    if (!ready) hydrate()
  }, [ready, hydrate])

  const invalidate = useCallback(() => {
    refresh()
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  }, [refresh, queryClient])

  const moveStage = useCallback(
    (id: string, stage: OpportunityStage) => {
      const next = crmEngine.moveStage(id, stage)
      invalidate()
      return next
    },
    [invalidate],
  )

  const contact = useCallback(
    (id: string) => {
      const result = crmEngine.contact(id)
      setContactDraft(result.draft)
      setSelectedId(id)
      setResponseOpen(true)
      invalidate()
      return result
    },
    [invalidate, setContactDraft, setSelectedId, setResponseOpen],
  )

  const registerResponse = useCallback(
    (
      id: string,
      result: FollowUpResult,
      options?: { notes?: string; lostReasonCode?: LostReasonCode; lostReasonNotes?: string },
    ) => {
      const response = crmEngine.registerResponse(id, result, options)
      setResponseOpen(false)
      invalidate()
      return response
    },
    [invalidate, setResponseOpen],
  )

  const timeline = useCallback((id: string) => crmEngine.timeline(id), [])
  const followUps = useCallback((id: string) => crmEngine.followUps(id), [])

  const exportCsv = useCallback(() => {
    const csv = crmEngine.exportCsv(filters)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `oportunidades-${Date.now()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [filters])

  const exportExcel = useCallback(() => {
    // Excel-compatible CSV (architecture; full XLSX reserved for future)
    exportCsv()
  }, [exportCsv])

  const exportPdf = useCallback(() => crmEngine.exportPdfMeta(filters), [filters])

  const exportJson = useCallback(() => {
    const payload = crmEngine.exportJson(filters)
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `oportunidades-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
    return payload
  }, [filters])

  const selected = opportunities.find((o) => o.id === selectedId) ?? null

  return {
    ready,
    opportunities: query.data ?? opportunities,
    pipeline,
    filters,
    setFilters,
    selected,
    selectedId,
    setSelectedId,
    contactDraft,
    responseOpen,
    setResponseOpen,
    permissions,
    moveStage,
    contact,
    registerResponse,
    timeline,
    followUps,
    exportCsv,
    exportExcel,
    exportPdf,
    exportJson,
    isLoading: query.isLoading,
    refresh: invalidate,
    engine: crmEngine,
  }
}
